import { Box, Chip, Grid, Typography } from '@material-ui/core'
import { ClosableDialog } from 'components/controls/dialogs'
import { SubscriptionPresenter } from 'components/domain/account'
import { useUser } from 'components/providers/UserProvider'
import CardmarketLogo from 'img/cardmarket-logo.svg'
import CardTraderLogo from 'img/cardtrader-logo.svg'
import { useAllGamesStockSummary } from 'lib/hooks/useAllGamesStockSummary'
import { useRelistingStore } from 'lib/hooks/useRelistingStore'
import { currencySymbol, isFromMainHost } from 'lib/utils'
import React, { useEffect, useState } from 'react'
import { Alert } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import './dashboard.css'
import { FreeTierPresenter } from './FreeTierPresenter'
import { GradeRelabelDialog } from './GradeRelabelDialog'
import { NewsPresenter } from './NewsPresenter'
import { NotificationCenter } from './NotificationCenter'

export const DashboardPresenter = (): JSX.Element => {
  const { user } = useUser()
  const { t } = useTranslation()
  const history = useHistory()
  // The changelog announcing the grade relabel links to /?relabel-grades, which opens the dialog here.
  const { search } = useLocation()
  const relabelOpen = new URLSearchParams(search).has('relabel-grades')

  const { showRelistingsBanner } = useRelistingStore()
  const [displayTaxationMessage, setDisplayTaxationMessage] = useState(
    user.enabledNotifications?.displayTaxationMessageInDashboard ?? false
  )

  const stockSummary = useAllGamesStockSummary()

  const [stockValueDialogOpen, setStockValueDialogOpen] = useState(false)
  const [importFileDialogOpen, setImportFileDialogOpen] = useState(true)

  const MAX_NUMBER_OF_CONFLICTS = 0
  const numberOfConflicts = user.ptInventory?.nightly?.numberOfConflicts || 0
  const resolved = user.ptInventory?.nightly?.resolved || false
  const showConflictsBanner = numberOfConflicts > MAX_NUMBER_OF_CONFLICTS && !resolved

  // Use primitive values in deps to avoid re-running on every user object reference change
  const isInventoryTester = user?.isTester?.ptInventory
  const isNotSynced = !user?.ptInventory
  useEffect(() => {
    if (isInventoryTester && isNotSynced) {
      history.push('/inventory')
    }
  }, [isInventoryTester, isNotSynced])

  const handleClick = (page: string): void => {
    history.push(page)
  }

  const invalidCurrencyPopupWindow = () => {
    const invalidShops = user.assignedShopifyShops.filter((shop) => shop.hasInvalidCurrency)
    if (invalidShops.length === 0) return <></>
    return (
      <ClosableDialog onClose={() => setImportFileDialogOpen(false)} open={importFileDialogOpen}>
        <Alert variant="error">{t('connectShopify.shopsWithWrongCurrency')}</Alert>
      </ClosableDialog>
    )
  }

  const formatStockValue = (value: number) => {
    return value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  const formatRenewalDate = (date: string | Date | null) => {
    if (!date) return '-'
    const d = new Date(date)
    return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`
  }

  return (
    <>
      {invalidCurrencyPopupWindow()}
      {relabelOpen && <GradeRelabelDialog onClose={() => history.replace('/')} />}
      <Box className="dashboard-presenter">
        <Grid container spacing={3} direction="row" alignItems="flex-start">
          <Grid item xs={12}>
            <div className="dashboard-header">
              <h2 className="fw-bold m-0">
                {t('dashboard.welcome', {
                  username:
                    user?.role === 'content_editor' ? 'shop@tcgpowertools.com' : user?.username,
                })}
              </h2>
              <div className="dashboard-channel-chips">
                <Chip
                  icon={<img src={CardmarketLogo} alt="Cardmarket" className="channel-chip-icon" />}
                  label={user?.cardmarketUsername || t('dashboard.channelNotConnected')}
                  variant="outlined"
                  className={`channel-chip${
                    !user?.cardmarketUsername ? ' channel-chip-inactive' : ''
                  }`}
                  {...(!user?.cardmarketUsername && {
                    clickable: true,
                    onClick: () => history.push('/account?tab=salesChannels'),
                  })}
                />
                {user?.featureFlags?.cardtrader && (
                  <Chip
                    icon={
                      <img src={CardTraderLogo} alt="CardTrader" className="channel-chip-icon" />
                    }
                    label={
                      user?.assignedCardtraderUser?.cardtraderUserName ||
                      t('dashboard.channelNotConnected')
                    }
                    variant="outlined"
                    className={`channel-chip${
                      !user?.assignedCardtraderUser?.cardtraderUserName
                        ? ' channel-chip-inactive'
                        : ''
                    }`}
                    {...(!user?.assignedCardtraderUser?.cardtraderUserName && {
                      clickable: true,
                      onClick: () => history.push('/account?tab=salesChannels'),
                    })}
                  />
                )}
                {user?.featureFlags?.shopify && (
                  <Chip
                    icon={<i className="fa-brands fa-shopify channel-chip-fa-icon" />}
                    label={
                      user?.assignedShopifyShops?.filter((s) => !s.disabled)?.[0]?.shop ||
                      t('dashboard.channelNotConnected')
                    }
                    variant="outlined"
                    className={`channel-chip${
                      !user?.assignedShopifyShops?.filter((s) => !s.disabled)?.length
                        ? ' channel-chip-inactive'
                        : ''
                    }`}
                    {...(!user?.assignedShopifyShops?.filter((s) => !s.disabled)?.length && {
                      clickable: true,
                      onClick: () => history.push('/account?tab=salesChannels'),
                    })}
                  />
                )}
              </div>
            </div>
          </Grid>

          {user.subscription.allFeaturesDisabled ? (
            <Grid item xs={12}>
              {isFromMainHost() && (
                <Box marginTop={5} width="450px" margin="0 auto">
                  <div className="dashboard-informative-card">
                    <FreeTierPresenter />
                  </div>
                  <Box margin="0 auto" marginTop={2}>
                    <Box padding={2}>
                      <Typography
                        style={{ textAlign: 'center' }}
                        color="primary"
                        gutterBottom
                        variant="body1"
                      >
                        <b>{t('dashboard.upgradeSubscription')}</b>
                      </Typography>
                      <Box className="dashboard-informative-card">
                        <SubscriptionPresenter subscription={user.subscription} centered />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
            </Grid>
          ) : (
            <>
              <Grid item xs={12} lg={9}>
                {(showConflictsBanner || showRelistingsBanner) && (
                  <NotificationCenter
                    showConflictsBanner={showConflictsBanner}
                    showRelistingsBanner={showRelistingsBanner}
                    numberOfConflicts={numberOfConflicts}
                    displayTaxationMessage={displayTaxationMessage}
                    setDisplayTaxationMessage={setDisplayTaxationMessage}
                  />
                )}

                <div
                  className="dashboard-info-boxes"
                  style={{
                    marginTop: showConflictsBanner || showRelistingsBanner ? '24px' : 0,
                  }}
                >
                  <div
                    className="info-box info-box-clickable"
                    onClick={() => !stockSummary.loading && setStockValueDialogOpen(true)}
                  >
                    <h2 className="fw-bold">
                      {stockSummary.loading ? (
                        <i className="fa-light fa-ellipsis fa-fade" />
                      ) : (
                        `${formatStockValue(stockSummary.stockValue)}${currencySymbol(
                          user?.currency
                        )}`
                      )}
                    </h2>
                    <span className="fs-6">{t('dashboard.totalStockValue')}</span>
                  </div>

                  <div className="info-box">
                    <div className="info-box-dual">
                      <div className="info-box-dual-row">
                        <h3>
                          {stockSummary.loading ? (
                            <i className="fa-light fa-ellipsis fa-fade" />
                          ) : (
                            stockSummary.uniqueArticles.toLocaleString()
                          )}
                        </h3>
                        <span className="fs-6">{t('dashboard.uniqueArticles')}</span>
                      </div>
                      <div className="info-box-dual-row">
                        <h3>
                          {stockSummary.loading ? (
                            <i className="fa-light fa-ellipsis fa-fade" />
                          ) : (
                            stockSummary.totalItems.toLocaleString()
                          )}
                        </h3>
                        <span className="fs-6">{t('dashboard.items')}</span>
                      </div>

                      <div className="info-box-dual-row">
                        <span className="fs-6">
                          {stockSummary.loading ? (
                            <i className="fa-light fa-ellipsis fa-fade" />
                          ) : (
                            stockSummary.soldItems.toLocaleString()
                          )}
                        </span>
                        <span className="fs-6">{t('dashboard.soldItems')}</span>
                      </div>
                    </div>
                  </div>

                  <div className="info-box">
                    <h6 className="m-0 p-0">{t('dashboard.plan')}</h6>
                    <h3 className="m-0 p-0 my-2 fw-bold">{user?.subscription?.name || '-'}</h3>
                    <div className="info-box-renewal">
                      <span>{t('dashboard.renewalDate')}</span>
                      <span>{formatRenewalDate(user?.subscription?.renewalDate)}</span>
                    </div>
                  </div>
                </div>
                <h3 className="mt-5">{t('dashboard.withPowertools')}</h3>
                <div className="dashboard-action-cards">
                  <div
                    className="dashboard-action-card"
                    onClick={() => handleClick('/listing-and-appraisal')}
                  >
                    <i className="fa-light fa-rectangle-history-circle-plus dashboard-action-icon" />
                    <h5 className="fw-bold secondary-color">{t('dashboard.addArticles')}</h5>
                    <span className="dashboard-action-desc">{t('dashboard.addArticlesDesc')}</span>
                  </div>
                  <div
                    className="dashboard-action-card"
                    onClick={() => handleClick('/listing-and-appraisal?importCsv=true')}
                  >
                    <i className="fa-light fa-file-arrow-up dashboard-action-icon" />
                    <h5 className="fw-bold secondary-color">{t('dashboard.importFile')}</h5>
                    <span className="dashboard-action-desc">{t('dashboard.importFileDesc')}</span>
                  </div>
                  <div
                    className="dashboard-action-card"
                    onClick={() => handleClick('/stock-pricing')}
                  >
                    <i className="fa-light fa-money-check-dollar dashboard-action-icon" />
                    <h5 className="fw-bold secondary-color">{t('dashboard.stockPricing')}</h5>
                    <span className="dashboard-action-desc">{t('dashboard.stockPricingDesc')}</span>
                  </div>
                  <div
                    className="dashboard-action-card"
                    onClick={() => handleClick('/pricing-settings?tab=pricing-bot')}
                  >
                    <i className="fa-light fa-robot dashboard-action-icon" />
                    <h5 className="fw-bold secondary-color">{t('dashboard.thePricingBot')}</h5>
                    <span className="dashboard-action-desc">
                      {t('dashboard.pricingBot.enabled')}
                    </span>
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} lg={3}>
                <NewsPresenter />
              </Grid>
            </>
          )}
        </Grid>
      </Box>

      <ClosableDialog
        open={stockValueDialogOpen}
        onClose={() => setStockValueDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box padding={3}>
          <h3 className="fw-bold mb-4">{t('dashboard.stockValueBreakdown')}</h3>
          <table className="stock-breakdown-table">
            <thead>
              <tr>
                <th>{t('dashboard.game')}</th>
                <th style={{ textAlign: 'right' }}>{t('dashboard.singles')}</th>
                <th style={{ textAlign: 'right' }}>{t('dashboard.sealed')}</th>
                <th style={{ textAlign: 'right' }}>{t('dashboard.total')}</th>
              </tr>
            </thead>
            <tbody>
              {stockSummary.gameBreakdown.map((game) => (
                <tr key={game.gameId}>
                  <td>{game.gameName}</td>
                  <td style={{ textAlign: 'right' }}>
                    {formatStockValue(game.singlesValue)}
                    {currencySymbol(user?.currency)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {formatStockValue(game.sealedValue)}
                    {currencySymbol(user?.currency)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <b>
                      {formatStockValue(game.totalValue)}
                      {currencySymbol(user?.currency)}
                    </b>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="stock-breakdown-total-row">
                <td>
                  <b>{t('dashboard.total')}</b>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <b>
                    {formatStockValue(
                      stockSummary.gameBreakdown.reduce((sum, g) => sum + g.singlesValue, 0)
                    )}
                    {currencySymbol(user?.currency)}
                  </b>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <b>
                    {formatStockValue(
                      stockSummary.gameBreakdown.reduce((sum, g) => sum + g.sealedValue, 0)
                    )}
                    {currencySymbol(user?.currency)}
                  </b>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <b>
                    {formatStockValue(stockSummary.stockValue)}
                    {currencySymbol(user?.currency)}
                  </b>
                </td>
              </tr>
            </tfoot>
          </table>
        </Box>
      </ClosableDialog>
    </>
  )
}

export const DashboardButton = React.memo(function DashboardButton({
  icon,
  title,
  redirect,
  description,
  handleClick,
}: {
  icon: string
  title: string
  redirect: string
  description: string
  handleClick: (page: string) => void
}) {
  return (
    <div className="dashboard-button-container" onClick={() => handleClick(`${redirect}`)}>
      <div className="d-flex flex-column justify-content-center align-items-center">
        <i className={icon} />
        <h4 className="fw-semibold">{title}</h4>
        <p className="fs-6">{description}</p>
      </div>
    </div>
  )
})
